import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import "./TaskVerification.css";
import { Tasks } from "./Tasks";
import {
  TASK_VERIFICATION_PAGE_SIZE,
  useGetPendingTaskQuery,
} from "../../../redux-toolkit/features/books/bookApiSlice";
import { useInfiniteScroll } from "../../../hooks/useInfiniteScroll";
import { useSelector } from "react-redux";
import { selectSelectedCompany } from "../../../redux-toolkit/features/auth/authSlice";
import { resolveActiveCompanyId } from "../../../utils/companyContextNavigation";
import ProgressBar from "../../UI/ProgressBar";
import { Error404 } from "../../UI/404/Error404";
import { NoRecord } from "../../UI/No record/NoRecord";
import {
  ORG_TASK_SORT_OPTIONS,
  TaskVerificationToolbar,
} from "./TaskVerificationToolbar";

const DEFAULT_TASK_SORT = ORG_TASK_SORT_OPTIONS[0];

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ padding: "20px 0px" }}>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const TaskVerificationList = ({
  taskData,
  taskType,
  isLoadingMore,
  sentinelRef,
}) => (
  <>
    <div className="task">
      {taskData?.data?.taskCompletionRequests?.map((curElm, index) => (
        <Tasks
          key={curElm?.user?._id || index}
          allTask={curElm}
          taskType={taskType}
        />
      ))}
    </div>
    <div ref={sentinelRef} />
    {isLoadingMore && (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          py: 2,
        }}
      >
        <ProgressBar />
      </Box>
    )}
  </>
);

TaskVerificationList.propTypes = {
  taskData: PropTypes.shape({
    data: PropTypes.shape({
      taskCompletionRequests: PropTypes.array,
    }),
  }),
  taskType: PropTypes.string.isRequired,
  isLoadingMore: PropTypes.bool,
  sentinelRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]),
};

export const TaskVerification = () => {
  const [value, setValue] = React.useState(0);
  const [page, setPage] = useState(1);
  const activeTaskType = value === 0 ? "ORG" : "COMPANY";
  const selectedCompany = useSelector(selectSelectedCompany);
  const user = useSelector((state) => state.auth.user);
  const activeCompanyId = resolveActiveCompanyId(selectedCompany, user);

  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [taskSort, setTaskSort] = useState(DEFAULT_TASK_SORT);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(searchInput.trim());
    }, 500);

    return () => clearTimeout(timer);
  }, [searchInput]);

  const {
    data: taskData,
    isLoading: taskLoading,
    isFetching: taskFetching,
    isError: taskError,
    refetch,
  } = useGetPendingTaskQuery(
    {
      type: activeTaskType,
      selectedCompany: activeCompanyId,
      page,
      limit: TASK_VERIFICATION_PAGE_SIZE,
      search: searchQuery,
      sortBy: taskSort.sortBy,
      sortOrder: taskSort.sortOrder,
    },
    {
      refetchOnMountOrArgChange: true,
      skip: !activeCompanyId,
    },
  );

  const pagination = taskData?.data?.pagination;
  const hasMore = pagination ? page < pagination.totalPages : false;

  const loadMore = useCallback(() => {
    if (!taskFetching && hasMore) {
      setPage((currentPage) => currentPage + 1);
    }
  }, [taskFetching, hasMore]);

  const sentinelRef = useInfiniteScroll({
    hasMore,
    isLoading: taskFetching,
    onLoadMore: loadMore,
  });

  useEffect(() => {
    setPage(1);
  }, [activeTaskType, activeCompanyId, searchQuery, taskSort.sortBy, taskSort.sortOrder]);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const isLoading = (taskLoading || (taskFetching && !taskData)) && page === 1;
  const isLoadingMore = taskFetching && page > 1;
  const isError = taskError;
  const hasTasks = (taskData?.data?.taskCompletionRequests?.length ?? 0) > 0;

  return (
    <div className="container">
      <div className="task__layout">
        <Box sx={{ width: "100%" }}>
          <Box
            sx={{
              borderBottom: 1,
              borderColor: "divider",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Tabs
              value={value}
              onChange={handleChange}
              aria-label="basic tabs example"
            >
              <Tab
                label="Organizational"
                sx={{
                  textTransform: "capitalize",
                  marginRight: "20px",
                  fontWeight: "500",
                }}
                {...a11yProps(0)}
              />
              <Tab
                sx={{ textTransform: "capitalize", fontWeight: "500" }}
                label="Company"
                {...a11yProps(1)}
              />
            </Tabs>

            <TaskVerificationToolbar
              searchVal={searchInput}
              setSearchVal={setSearchInput}
              sortValue={taskSort}
              onSortChange={setTaskSort}
              sortOptions={ORG_TASK_SORT_OPTIONS}
            />
          </Box>

          <TabPanel value={value} index={0}>
            {isLoading && value === 0 ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  minHeight: "200px",
                }}
              >
                <ProgressBar />
              </Box>
            ) : isError && value === 0 ? (
              <Error404 />
            ) : hasTasks ? (
              <TaskVerificationList
                taskData={taskData}
                taskType={activeTaskType}
                isLoadingMore={isLoadingMore && value === 0}
                sentinelRef={value === 0 ? sentinelRef : null}
              />
            ) : (
              <NoRecord />
            )}
          </TabPanel>

          <TabPanel value={value} index={1}>
            {isLoading && value === 1 ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  minHeight: "200px",
                }}
              >
                <ProgressBar />
              </Box>
            ) : isError && value === 1 ? (
              <Error404 />
            ) : hasTasks ? (
              <TaskVerificationList
                taskData={taskData}
                taskType={activeTaskType}
                isLoadingMore={isLoadingMore && value === 1}
                sentinelRef={value === 1 ? sentinelRef : null}
              />
            ) : (
              <NoRecord />
            )}
          </TabPanel>
        </Box>
      </div>
    </div>
  );
};
